import { useState } from 'react';
import { ArrowLeft, ArrowUp, ArrowDown, Heart, Trash2, Pin, Pencil, Bot } from 'lucide-react';
import { Discussion, discussionService } from '@services/discussionService';
import { useAuthStore } from '@store/authStore';
import { useDiscussionStore } from '@store/discussionStore';
import CommentSection from './CommentSection';
import DiscussionImage from './DiscussionImage';
import MarkdownContent from '@components/common/MarkdownContent';
import AdminInstructionModal from '@components/common/AdminInstructionModal';
import { formatDateTime } from '@utils/dateUtils';
import toast from 'react-hot-toast';

interface DiscussionDetailProps {
    discussion: Discussion | null;
    loading: boolean;
    onBack: () => void;
    onVote: (discussionId: string, voteType: 'up' | 'down') => void;
    onDelete: (discussionId: string) => void;
}

// Content length limits (must match backend)
const MIN_DISCUSSION_LENGTH = 20;
const MAX_CONTENT_LENGTH = 5000;

const TYPE_COLORS = {
    bug: 'red',
    feature: 'yellow',
    question: 'blue',
    feedback: 'green',
    astrology: 'indigo',
    general: 'purple',
};

const DISCUSSION_TYPES = [
    { value: 'general', label: 'General', activeClass: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
    { value: 'announcement', label: 'Announcement', activeClass: 'bg-orange-500/20 text-orange-300 border-orange-500/50', adminOnly: true },
    { value: 'bug', label: 'Bug', activeClass: 'bg-red-500/20 text-red-300 border-red-500/50' },
    { value: 'feature', label: 'Feature', activeClass: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
    { value: 'question', label: 'Question', activeClass: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
    { value: 'feedback', label: 'Feedback', activeClass: 'bg-green-500/20 text-green-300 border-green-500/50' },
    { value: 'astrology', label: 'Astrology', activeClass: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50' },
];

const DiscussionDetail = ({ discussion, loading, onBack, onVote, onDelete }: DiscussionDetailProps) => {
    const { user, isAuthenticated } = useAuthStore();
    const { updateDiscussionHeart, updateDiscussionPin, updateDiscussionContent } = useDiscussionStore();
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editTypes, setEditTypes] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [triggeringBot, setTriggeringBot] = useState(false);
    const [isBotModalOpen, setIsBotModalOpen] = useState(false);
    const [botTriggerCount, setBotTriggerCount] = useState(0);

    const startEditing = () => {
        if (discussion) {
            setEditContent(discussion.content);
            setEditTypes(discussion.types || [discussion.type]);
            setIsEditing(true);
        }
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditContent('');
        setEditTypes([]);
    };

    const toggleEditType = (typeValue: string) => {
        setEditTypes(prev => {
            if (prev.includes(typeValue)) {
                // Don't allow removing if it's the last type
                if (prev.length === 1) {
                    toast.error('At least one type must be selected');
                    return prev;
                }
                return prev.filter(t => t !== typeValue);
            } else {
                return [...prev, typeValue];
            }
        });
    };

    const saveEdit = async () => {
        if (!discussion || !editContent.trim()) {
            toast.error('Content cannot be empty');
            return;
        }

        if (editContent.trim().length < MIN_DISCUSSION_LENGTH) {
            toast.error(`Content must be at least ${MIN_DISCUSSION_LENGTH} characters`);
            return;
        }

        setSaving(true);
        try {
            await discussionService.editDiscussion(discussion.id, {
                types: editTypes,
                content: editContent.trim(),
            });
            toast.success('Discussion updated');
            setIsEditing(false);
            // Update store directly instead of refetching
            updateDiscussionContent(discussion.id, editTypes[0], editContent.trim());
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to update discussion');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-96 bg-white/[0.08] rounded-3xl border border-white/20 animate-pulse" />
            </div>
        );
    }

    if (!discussion) {
        return (
            <div className="text-center py-20">
                <p className="text-purple-300/60 font-sans">Discussion not found</p>
            </div>
        );
    }

    const types = discussion.types || [discussion.type];
    const primaryType = types[0] || discussion.type;
    const typeColor = TYPE_COLORS[primaryType] || 'purple';
    const hasUpvoted = discussion.upvotes?.includes(user?._id || '');
    const hasDownvoted = discussion.downvotes?.includes(user?._id || '');
    const isOwner = user?._id === discussion.user_id;
    const isAdmin = user?.is_admin;

    const handleAdminHeart = async () => {
        if (!isAdmin || !discussion) return;
        try {
            const response = await discussionService.toggleAdminHeart(discussion.id);
            // Update store directly instead of refetching
            updateDiscussionHeart(discussion.id, response.admin_hearted);
        } catch (error: any) {
            console.error('Failed to toggle heart:', error);
        }
    };

    const handleTogglePin = async () => {
        if (!isAdmin || !discussion) return;
        try {
            const response = await discussionService.togglePin(discussion.id);
            toast.success(response.pinned ? 'Discussion pinned' : 'Discussion unpinned');
            // Update store directly instead of refetching
            updateDiscussionPin(discussion.id, response.pinned);
        } catch (error: any) {
            toast.error('Failed to toggle pin');
            console.error('Failed to toggle pin:', error);
        }
    };

    const handleTriggerBot = async (adminInstruction?: string) => {
        if (!isAdmin || !discussion) return;
        setTriggeringBot(true);
        try {
            await discussionService.triggerBotResponse(discussion.id, adminInstruction);
            toast.success('Bot response triggered! It will appear shortly.');
            setIsBotModalOpen(false);
            setBotTriggerCount(c => c + 1);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to trigger bot response');
        } finally {
            setTriggeringBot(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Back Button */}
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors font-sans"
            >
                <ArrowLeft size={20} />
                <span>Back to discussions</span>
            </button>

            {/* Discussion Card */}
            <div className="bg-white/[0.08] border border-white/20 rounded-3xl p-4 md:p-8 relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-64 h-64 bg-${typeColor}-500/10 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2`} />

                <div className="relative z-10">
                    {/* Header */}
                    <div className="space-y-4 mb-6">
                        {/* User Info Row */}
                        <div className="flex items-start gap-3">
                            {/* User Avatar */}
                            {discussion.user_profile_picture && discussion.user_profile_picture.trim() !== '' ? (
                                <img
                                    src={discussion.user_profile_picture}
                                    alt={discussion.user_name}
                                    className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/20 object-cover flex-shrink-0"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (fallback) fallback.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div
                                className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold flex-shrink-0"
                                style={{ display: discussion.user_profile_picture && discussion.user_profile_picture.trim() !== '' ? 'none' : 'flex' }}
                            >
                                {discussion.user_name?.[0]?.toUpperCase() || 'U'}
                            </div>

                            <div className="flex-1 min-w-0">
                                {/* Name and Tag Row */}
                                <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                                    <div className="flex flex-col">
                                        <span className="text-white font-bold font-sans text-base md:text-lg">
                                            {discussion.user_name}
                                        </span>
                                        {discussion.username && (
                                            <span className="text-purple-300/50 text-xs font-sans">
                                                @{discussion.username}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* Display all types */}
                                        {types.map((type) => {
                                            const color = TYPE_COLORS[type] || 'purple';
                                            return (
                                                <span key={type} className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-${color}-500/20 text-${color}-300 border border-${color}-500/30 flex-shrink-0`}>
                                                    {type}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Time Row */}
                                <div>
                                    <span className="text-purple-300/60 text-xs md:text-sm font-sans">
                                        {formatDateTime(discussion.created_at)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Actions Row - Only show if user has permissions */}
                        {(isAdmin || isOwner) && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {isAdmin && (
                                    <>
                                        <button
                                            onClick={handleTogglePin}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-sm font-sans ${discussion.pinned
                                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                                : 'bg-white/5 hover:bg-white/10 text-white/60 border border-white/10'
                                                }`}
                                        >
                                            <Pin size={16} className={discussion.pinned ? 'fill-purple-400' : ''} />
                                            <span className="hidden sm:inline">{discussion.pinned ? 'Unpin' : 'Pin'}</span>
                                        </button>
                                        <button
                                            onClick={handleAdminHeart}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-sm font-sans ${discussion.admin_hearted
                                                ? 'bg-red-500/20 text-red-500 border border-red-500/30'
                                                : 'bg-white/5 hover:bg-white/10 text-white/60 border border-white/10'
                                                }`}
                                        >
                                            <Heart size={16} className={discussion.admin_hearted ? 'fill-red-500' : ''} />
                                            <span className="hidden sm:inline">{discussion.admin_hearted ? 'Unheart' : 'Heart'}</span>
                                        </button>
                                        <button
                                            onClick={() => setIsBotModalOpen(true)}
                                            disabled={triggeringBot}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all text-sm font-sans border border-cyan-500/30 disabled:opacity-50"
                                        >
                                            <Bot size={16} />
                                            <span className="hidden sm:inline">{triggeringBot ? 'Triggering...' : 'AI Reply'}</span>
                                        </button>
                                    </>
                                )}

                                {(isOwner || isAdmin) && !isEditing && (
                                    <button
                                        onClick={startEditing}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-all text-sm font-sans border border-blue-500/30"
                                    >
                                        <Pencil size={16} />
                                        <span className="hidden sm:inline">Edit</span>
                                    </button>
                                )}

                                {(isOwner || isAdmin) && (
                                    <button
                                        onClick={() => onDelete(discussion.id)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all text-sm font-sans border border-red-500/30"
                                    >
                                        <Trash2 size={16} />
                                        <span className="hidden sm:inline">Delete</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pinned Badge */}
                    {discussion.pinned && (
                        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-xl w-fit">
                            <Pin size={16} className="text-purple-400 fill-purple-400" />
                            <span className="text-sm text-purple-400 font-sans font-bold">Pinned by Admin</span>
                        </div>
                    )}

                    {/* Admin Heart Badge */}
                    {discussion.admin_hearted && (
                        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl w-fit">
                            <Heart size={16} className="text-red-500 fill-red-500" />
                            <span className="text-sm text-red-400 font-sans font-bold">Loved by Admin</span>
                        </div>
                    )}

                    {/* Content */}
                    {isEditing ? (
                        <div className="mb-6 bg-white/[0.04] border border-white/10 rounded-2xl p-4 space-y-4">
                            {/* Type Badges - Multi-select */}
                            <div className="flex flex-wrap gap-2 pb-3 border-b border-white/10">
                                <span className="text-xs text-purple-300/60 font-sans self-center mr-1">
                                    Select tags:
                                </span>
                                {DISCUSSION_TYPES.filter(t => !t.adminOnly || user?.is_admin).map((t) => (
                                    <button
                                        key={t.value}
                                        onClick={() => toggleEditType(t.value)}
                                        disabled={saving}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase transition-all border-2 ${editTypes.includes(t.value)
                                            ? t.activeClass
                                            : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20 hover:text-white/60'
                                            } disabled:opacity-50`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {/* Content Editor */}
                            <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                placeholder="Share your thoughts..."
                                rows={5}
                                autoFocus
                                maxLength={MAX_CONTENT_LENGTH}
                                className="w-full bg-transparent border-none text-white text-base placeholder:text-white/30 focus:outline-none font-sans resize-none"
                                disabled={saving}
                            />
                            {/* Character Counter - Twitter style */}
                            <div className="flex items-center justify-end gap-2 mt-1">
                                {editContent.length < MIN_DISCUSSION_LENGTH && (
                                    <span className="text-xs text-yellow-400/80 font-sans">
                                        Min {MIN_DISCUSSION_LENGTH} chars
                                    </span>
                                )}
                                <span className={`text-xs font-sans ${
                                    editContent.length < MIN_DISCUSSION_LENGTH
                                        ? 'text-yellow-400'
                                        : editContent.length > MAX_CONTENT_LENGTH - 100
                                            ? 'text-red-400'
                                            : editContent.length > MAX_CONTENT_LENGTH - 500
                                                ? 'text-yellow-400'
                                                : 'text-purple-300/60'
                                }`}>
                                    {editContent.length}/{MAX_CONTENT_LENGTH}
                                </span>
                            </div>

                            {/* Divider */}
                            <div className="border-t border-white/10" />

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={cancelEditing}
                                    disabled={saving}
                                    className="px-4 py-2 text-white/60 hover:text-white font-bold rounded-full transition-all text-sm font-sans disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveEdit}
                                    disabled={saving || !editContent.trim() || editContent.trim().length < MIN_DISCUSSION_LENGTH}
                                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-2 px-6 rounded-full transition-all shadow-lg text-sm font-sans disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-6 text-base md:text-lg font-sans">
                            <MarkdownContent content={discussion.content} />
                        </div>
                    )}

                    {/* Image */}
                    {discussion.image_filename && discussion.user_id && (
                        <div className="mb-6 flex justify-center">
                            <DiscussionImage
                                filename={discussion.image_filename}
                                userId={discussion.user_id}
                                alt="Discussion image"
                                className="max-h-[500px] w-auto rounded-2xl border border-white/20 block"
                            />
                        </div>
                    )}

                    {/* Voting */}
                    <div className="flex items-center gap-2 md:gap-3">
                        <button
                            onClick={() => onVote(discussion.id, 'up')}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-xl transition-all font-sans text-sm ${hasUpvoted
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                                }`}
                        >
                            <ArrowUp size={16} className="md:w-[18px] md:h-[18px]" />
                            <span className="font-bold">{discussion.upvote_count}</span>
                            <span className="hidden md:inline">Upvote{discussion.upvote_count !== 1 ? 's' : ''}</span>
                        </button>

                        <button
                            onClick={() => onVote(discussion.id, 'down')}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-xl transition-all font-sans text-sm ${hasDownvoted
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                                }`}
                        >
                            <ArrowDown size={16} className="md:w-[18px] md:h-[18px]" />
                            <span className="font-bold">{discussion.downvote_count}</span>
                            <span className="hidden md:inline">Downvote{discussion.downvote_count !== 1 ? 's' : ''}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Comments Section */}
            <CommentSection
                discussionId={discussion.id}
                comments={discussion.comments || []}
                discussionOwner={{
                    user_id: discussion.user_id,
                    user_name: discussion.user_name,
                    user_profile_picture: discussion.user_profile_picture
                }}
                botTriggerCount={botTriggerCount}
            />

            {isAdmin && (
                <AdminInstructionModal
                    isOpen={isBotModalOpen}
                    onClose={() => setIsBotModalOpen(false)}
                    onSubmit={async (instruction) => handleTriggerBot(instruction)}
                    loading={triggeringBot}
                    title="Edit Prompt"
                    placeholder="Describe how you'd like AI to reply (optional). Example: User asked pricing. Show all plans and suggest Quick for budget users."
                />
            )}
        </div>
    );
};

export default DiscussionDetail;
